import { Add, Delete, Edit } from "@mui/icons-material";
import SmsIcon from "@mui/icons-material/Sms";
import EmailIcon from "@mui/icons-material/Email";

import BlindsClosedIcon from "@mui/icons-material/BlindsClosed";
import {
  Avatar,
  Box,
  Grid,
  IconButton,
  Skeleton,
  Stack,
  TableCell,
  Tooltip,
  Typography,
} from "@mui/material";
import EmptyContent from "components/EmptyContent";
import List from "components/UI/List";
import { useAlert } from "contexts/alertContext";
import { Feature } from "models/api";
import { ICompany, ICompanyInput } from "models/company";
import React, { useCallback, useEffect, useState } from "react";
import { QueryClient, useMutation } from "react-query";
import BlockIcon from "@mui/icons-material/Block";
import {
  CompanyCells,
  addCompany,
  deleteCompany,
  editCompany,
  editCompanyColors,
  editCompanySMS,
  editCompanySMTP,
  getCompanies,
} from "framework/company";
import { getIndustries } from "framework/industries";
import Search from "components/Search/Search";
import AddCompany from "./partials/AddCompany/AddCompany";
import EditCompany from "./partials/EditCompany/EditCompany";
import DeleteCompany from "./partials/DeleteCompany/DeleteCompany";
import { getPermission, getUserData } from "helpers";
import { handleError } from "helpers/utils";
import { useTranslation } from "react-i18next";
import { IIndustry } from "models/industry";
import EditSMS from "./partials/EditSMS/EditSMS";
import EditSMTP from "./partials/EditSMTP/EditSMTP";
import ColorLensIcon from "@mui/icons-material/ColorLens";
import EditColors from "./partials/EditColors/EditColors";
import useFetchSection from "hooks/useFetchSection";

interface Props {
  feature: Feature;
}

const Companies: React.FC<Props> = ({ feature }) => {
  const [loading2, setLoading2] = useState<boolean>(false);
  const [FilterCompanies, setFilterCompanies] = useState<ICompany[]>(
    [] as ICompany[]
  );
  const [openAdd, setOpenAdd] = useState<boolean>(false);
  const [openEdit, setOpenEdit] = useState<boolean>(false);
  const [openDelete, setOpenDelete] = useState<boolean>(false);
  const [openSms, setOpenSms] = useState<boolean>(false);
  const [openSMTP, setOpenSMTP] = useState<boolean>(false);
  const [openColors, setOpenColors] = useState<boolean>(false);
  const [selectedCompany, setSelectedCompany] = useState<ICompany>(
    {} as ICompany
  );
  const [inputObj, setInputObj] = useState<ICompanyInput>({} as ICompanyInput);
  const { setAlert } = useAlert();
  const [industries, setIndustries] = useState<IIndustry[]>([] as IIndustry[]);
  const { t, i18n } = useTranslation();
  const user = getUserData();
  const { colors } = user;
  // eslint-disable-next-line
  const queryClient = new QueryClient();

  const addCompaniesMutation = useMutation({
    mutationFn: (createInput: FormData) => {
      return addCompany(createInput);
    },
  });

  const editCompaniesMutation = useMutation({
    mutationFn: (data: { createInput: FormData; id: string }) => {
      return editCompany(data.createInput, data.id);
    },
  });

  const editCompaniesSmsMutation = useMutation({
    mutationFn: (data: Partial<ICompanyInput>) => {
      return editCompanySMS(data);
    },
  });

  const editCompaniesSmtpMutation = useMutation({
    mutationFn: (data: Partial<ICompanyInput>) => {
      return editCompanySMTP(data);
    },
  });

  const editCompaniesColorsMutation = useMutation({
    mutationFn: (data: Partial<ICompanyInput>) => {
      return editCompanyColors(data);
    },
  });

  const deleteCompaniesMutation = useMutation({
    mutationFn: (code: string) => {
      return deleteCompany(code);
    },
  });

  const fetchIndustries = useCallback(async () => {
    try {
      let response: any = await queryClient.fetchQuery("get industries", () =>
        getIndustries()
      );
      setIndustries(response);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
    }

    // eslint-disable-next-line
  }, [queryClient, setAlert]);

  const {
    loading,
    data: Companies,
    error,
    permission,
    revalidate,
  } = useFetchSection({
    fetchData: () => getCompanies(),
    fetchPermission: () => getPermission(11),
    section: "Companies",
    onSuccess: (data) => setFilterCompanies(data),
  });

  useEffect(() => {
    fetchIndustries();
  }, []);
  if (loading)
    return (
      <Stack spacing={2} p={2}>
        {Array.from({ length: 10 }).map((_, i) => (
          <Skeleton variant="rectangular" key={i} width="100%" height={30} />
        ))}
      </Stack>
    );

  const handleChange = (name: string, value: any) => {
    if (name === 'IsCorporate') {
      setInputObj((prev) => ({
        ...prev,
        [name]: value || false,
      }));
      return
    }
    setInputObj((prev) => ({
      ...prev,
      [name]: value,
    }));
  };
  const handleOpenAdd = () => setOpenAdd(true);
  const handleCloseAdd = () => setOpenAdd(false);

  const handleOpenEdit = (Company: ICompany) => {
    setSelectedCompany(Company);
    setOpenEdit(true);
  };
  const handleOpenSms = (Company: ICompany) => {
    setSelectedCompany(Company);
    setOpenSms(true);
  };
  const handleOpenSMTP = (Company: ICompany) => {
    setSelectedCompany(Company);
    setOpenSMTP(true);
  };

  const handleOpenColors = (Company: ICompany) => {
    setSelectedCompany(Company);
    setOpenColors(true);
  };

  const handleCloseEdit = () => {
    setOpenEdit(false);
    setSelectedCompany({} as ICompany);
  };

  const handleCloseColors = () => {
    setOpenColors(false);
    setSelectedCompany({} as ICompany);
  };

  const handleCloseSms = () => {
    setOpenSms(false);
    setSelectedCompany({} as ICompany);
  };

  const handleCloseSMTP = () => {
    setOpenSMTP(false);
    setSelectedCompany({} as ICompany);
  };

  const handleOpenDelete = (Company: ICompany) => {
    setSelectedCompany(Company);
    setOpenDelete(true);
  };

  const handleCloseDelete = () => {
    setOpenDelete(false);
    setSelectedCompany({} as ICompany);
  };

  const getBodyCells = (row: ICompany) => {
    let cells = Object.keys(row).map((key: string, i: number) => {
      if (key === "logo")
        return {
          id: "logo",
          element: (
            <TableCell align={"left"} key={i}>
              {row.logo ? (
                <Box
                  component={"img"}
                  src={row?.logo}
                  alt={row?.name}
                  width={50}
                  height={50}
                />
              ) : (
                <Avatar sx={{ bgcolor: "primary.main" }}>
                  {row?.name?.charAt(0)}
                </Avatar>
              )}
            </TableCell>
          ),
        };
      if (key === "name")
        return {
          id: "name",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row?.name}</Typography>
            </TableCell>
          ),
        };

      if (key === "nameAr")
        return {
          id: "nameAr",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row?.nameAr||""}</Typography>
            </TableCell>
          ),
        };

      if (key === "industryName")
        return {
          id: "industryName",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row?.industryName}</Typography>
            </TableCell>
          ),
        };

      if (key === "industryNameAr")
        return {
          id: "industryNameAr",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row?.industryNameAr}</Typography>
            </TableCell>
          ),
        };

      if (key === "isCorporate")
        return {
          id: "isCorporate",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row?.isCorporate ? 'Yes' : 'No'}</Typography>
            </TableCell>
          ),
        };

      if (key === "id")
        return {
          id: "actions",
          element: (
            <TableCell align={"left"} key={i}>
              <Stack
                direction={"row"}
                alignContent={"center"}
                spacing={1}
                justifyContent={i18n.language === "en" ? "start" : "end"}
              >
                {permission?.hasModifyPermission && (
                  <Tooltip title={t("T_edit")}>
                    <IconButton
                      size={"small"}
                      color={"primary"}
                      onClick={() => handleOpenEdit(row)}
                    >
                      <Edit />
                    </IconButton>
                  </Tooltip>
                )}
                {permission?.hasModifyPermission && (
                  <Tooltip title="SMS">
                    <IconButton
                      size={"small"}
                      color={"warning"}
                      onClick={() => handleOpenSms(row)}
                    >
                      <SmsIcon />
                    </IconButton>
                  </Tooltip>
                )}
                {permission?.hasModifyPermission && (
                  <Tooltip title="SMTP">
                    <IconButton
                      size={"small"}
                      onClick={() => handleOpenSMTP(row)}
                    >
                      <EmailIcon />
                    </IconButton>
                  </Tooltip>
                )}
                {permission?.hasModifyPermission && (
                  <Tooltip title="Colors">
                    <IconButton
                      size={"small"}
                      color="success"
                      onClick={() => handleOpenColors(row)}
                    >
                      <ColorLensIcon />
                    </IconButton>
                  </Tooltip>
                )}
                {permission?.hasDeletePermission && (
                  <Tooltip title={t("T_delete")}>
                    <IconButton
                      size={"small"}
                      sx={{
                        color: colors.deleteIconColor
                          ? colors.deleteIconColor
                          : "red",
                        "&:hover": { color: colors?.deleteIconHoverColor },
                      }}
                      onClick={() => handleOpenDelete(row)}
                    >
                      <Delete />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            </TableCell>
          ),
        };
    });
    return cells;
  };

  const handleAdd = async () => {
    if (!inputObj.Name || !inputObj.Logo || !inputObj.IndustryId)
      return setAlert({
        open: true,
        message: "Please fill all fields",
        type: "error",
      });
    try {
      setLoading2(true);

      const createInput = new FormData();
      createInput.append("Name", inputObj.Name);
      if(inputObj.NameAr) createInput.append("NameAr", inputObj.NameAr);
      createInput.append("IndustryId", inputObj.IndustryId);
      createInput.append("IsCorporate", inputObj.IsCorporate ? 'true' : 'false');
      if (inputObj.Logo) createInput.append("Logo", inputObj.Logo[0] || inputObj.Logo);
      if (inputObj.CertificateName) createInput.append("CertificateName", inputObj.CertificateName);
      await addCompaniesMutation.mutateAsync(createInput);
      setAlert({
        open: true,
        message: "Company added successfully",
        type: "success",
      });
      revalidate();
      setInputObj({} as ICompanyInput);
      handleCloseAdd();
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: error.Message || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleEdit = async () => {

    try {
      setLoading2(true);
      const createInput = new FormData();
      if(inputObj.Name) createInput.append("Name", inputObj.Name);
      createInput.append("NameAr", inputObj.NameAr||"");
      if(inputObj.IndustryId) createInput.append("IndustryId",inputObj.IndustryId);
      createInput.append(
        "IsCorporate",
        inputObj.IsCorporate ?? selectedCompany.isCorporate ? 'true' : 'false'
      );
      if (inputObj.Logo) createInput.append("Logo", inputObj.Logo[0] || inputObj.Logo);
      if (inputObj.CertificateName !== undefined) createInput.append("CertificateName", inputObj.CertificateName || "");
      await editCompaniesMutation.mutateAsync({
        createInput,
        id: selectedCompany.id,
      });
      setAlert({
        open: true,
        message: "Company updated successfully",
        type: "success",
      });
      revalidate();
      handleCloseEdit();
      setInputObj({} as ICompanyInput);
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleEditSMS = async () => {
    try {
      setLoading2(true);
      const createInput = {
        ...inputObj,
        companyId: selectedCompany.id,
        twilioAccountSid:
          inputObj.twilioAccountSid || selectedCompany.twilioAccountSid,
        twilioAuthToken:
          inputObj.twilioAuthToken || selectedCompany.twilioAuthToken,
        twilioFromSenderName:
          inputObj.twilioFromSenderName || selectedCompany.twilioFromSenderName,
        twilioWhatsappFromMobile:
          inputObj.twilioWhatsappFromMobile ||
          selectedCompany.twilioWhatsappFromMobile,
      };

      const filteredCreateInput = Object.fromEntries(
        Object.entries(createInput).filter(([_, value]) => value !== null)
      );
      await editCompaniesSmsMutation.mutateAsync(filteredCreateInput);
      setAlert({
        open: true,
        message: "Company SMS updated successfully",
        type: "success",
      });
      revalidate();
      handleCloseSms();
      setInputObj({} as ICompanyInput);
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleEditSMTP = async () => {
    try {
      setLoading2(true);
      const createInput = {
        ...inputObj,
        companyId: selectedCompany.id,
        smtpHost: inputObj.smtpHost || selectedCompany.smtpHost,
        smtpPort: +inputObj.smtpPort || +selectedCompany.smtpPort,
        smtpUsername: inputObj.smtpUsername || selectedCompany.smtpUsername,
        smtpPassword: inputObj.smtpPassword || selectedCompany.smtpPassword,
        smtpFromEmail: inputObj.smtpFromEmail || selectedCompany.smtpFromEmail,
      };

      const filteredCreateInput = Object.fromEntries(
        Object.entries(createInput).filter(([_, value]) => value !== null)
      );

      await editCompaniesSmtpMutation.mutateAsync(filteredCreateInput);
      setAlert({
        open: true,
        message: "Company SMTP updated successfully",
        type: "success",
      });
      revalidate();
      handleCloseSMTP();
      setInputObj({} as ICompanyInput);
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: error.Message || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleEditColors = async () => {
    try {
      setLoading2(true);
      const createInput = {
        ...inputObj,
        companyId: selectedCompany.id,
        menuBgColor: inputObj.menuBgColor || selectedCompany.menuBgColor,
        menuItemHoverColor:
          inputObj.menuItemHoverColor || selectedCompany.menuItemHoverColor,
        menuItemSelectedColor:
          inputObj.menuItemSelectedColor ||
          selectedCompany.menuItemSelectedColor,
        deleteIconColor:
          inputObj.deleteIconColor || selectedCompany.deleteIconColor,
        deleteIconHoverColor:
          inputObj.deleteIconHoverColor || selectedCompany.deleteIconHoverColor,
        viewIconColor: inputObj.viewIconColor || selectedCompany.viewIconColor,
        viewIconHoverColor:
          inputObj.viewIconHoverColor || selectedCompany.viewIconHoverColor,
        addUpdateIconColor:
          inputObj.addUpdateIconColor || selectedCompany.addUpdateIconColor,
        addUpdateIconHoverColor:
          inputObj.addUpdateIconHoverColor ||
          selectedCompany.addUpdateIconHoverColor,
        cancelButtonColor:
          inputObj.cancelButtonColor || selectedCompany.cancelButtonColor,
        cancelButtonHoverColor:
          inputObj.cancelButtonHoverColor ||
          selectedCompany.cancelButtonHoverColor,
        labelColor: inputObj.labelColor || selectedCompany.labelColor,
        chartMainColor:
          inputObj.chartMainColor || selectedCompany.chartMainColor,
      };
      await editCompaniesColorsMutation.mutateAsync(createInput);
      setAlert({
        open: true,
        message: "Company colors updated successfully",
        type: "success",
      });
      revalidate();
      handleCloseColors();
      setInputObj({} as ICompanyInput);
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: error.Message || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleDelete = async () => {
    try {
      setLoading2(true);
      await deleteCompaniesMutation.mutateAsync(selectedCompany.id);
      setAlert({
        open: true,
        message: "Company deleted successfully",
        type: "success",
      });
      revalidate();
      handleCloseDelete();
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleSearch = async (value: string) => {
    if (!value) return setFilterCompanies(Companies!);
    const search = Companies!.filter((item) =>
      item.name.toLowerCase().includes(value.toLowerCase())
      || item.nameAr?.toLowerCase()?.includes(value.toLowerCase())
      || item.industryName?.toLowerCase()?.includes(value.toLowerCase())
      || item.industryNameAr?.toLowerCase()?.includes(value.toLowerCase())
    );
    setFilterCompanies(search);
  };

  return (
    <>
      <Stack spacing={2} p={2}>
        <Grid
          container
          justifyContent={"space-between"}
          alignItems="center"
          sx={{ mb: 4 }}
        >
          <Typography
            variant="h3"
            color={colors?.labelColor ? colors?.labelColor : "header.primary"}
          >
            {t(feature.name)}
          </Typography>
          <Stack direction={"row"} spacing={1}>
            <Search handleChange={handleSearch} />
            {permission?.hasModifyPermission && (
              <Tooltip title={t("T_add_company")}>
                <IconButton
                  sx={{
                    color: colors.addUpdateIconColor
                      ? colors.addUpdateIconColor
                      : "red",
                    "&:hover": { color: colors?.addUpdateIconHoverColor },
                    height: 40,
                    width: 40,
                    transition: "0.5s",
                  }}
                  onClick={handleOpenAdd}
                >
                  <Add />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Grid>

        {permission?.hasReadPermission! && FilterCompanies.length > 0 && (
          <List
            headCells={CompanyCells}
            data={FilterCompanies}
            getBodyCells={getBodyCells}
            feature={feature}
            canEdit={false}
          />
        )}
        {(!FilterCompanies || FilterCompanies.length === 0) &&
          permission?.hasReadPermission! && (
            <EmptyContent
              icon={<BlindsClosedIcon />}
              header="No Companies Found"
              message="There are no Companies available"
            />
          )}

        {!permission?.hasReadPermission && (
          <EmptyContent
            icon={<BlockIcon sx={{ fontSize: "3.5rem " }} />}
            header={"T_no_permissions"}
            message={"T_no_permissions_message"}
          />
        )}
      </Stack>

      {openAdd && (
        <AddCompany
          open={openAdd}
          loading={loading2}
          industries={industries}
          onClose={handleCloseAdd}
          onSubmit={handleAdd}
          onChanges={handleChange}
        />
      )}
      {openEdit && (
        <EditCompany
          open={openEdit}
          loading={loading2}
          company={selectedCompany}
          industries={industries}
          onClose={handleCloseEdit}
          onSubmit={handleEdit}
          onChanges={handleChange}
        />
      )}
      {openSms && (
        <EditSMS
          open={openSms}
          loading={loading2}
          company={selectedCompany}
          onClose={handleCloseSms}
          onSubmit={handleEditSMS}
          onChanges={handleChange}
        />
      )}
      {openSMTP && (
        <EditSMTP
          open={openSMTP}
          loading={loading2}
          company={selectedCompany}
          onClose={handleCloseSMTP}
          onSubmit={handleEditSMTP}
          onChanges={handleChange}
        />
      )}
      {openColors && (
        <EditColors
          open={openColors}
          loading={loading2}
          company={selectedCompany}
          onClose={handleCloseColors}
          onSubmit={handleEditColors}
          onChanges={handleChange}
        />
      )}
      {openDelete && (
        <DeleteCompany
          open={openDelete}
          loading={loading2}
          onClose={handleCloseDelete}
          onSubmit={handleDelete}
        />
      )}
    </>
  );
};

export default Companies;
